import { downloadUrlAsFile } from '@dealstack/frontend/shared/files/downloadFile';
import { AsyncTaskProgressDrawer } from '@dealstack/frontend/shared/components/AsyncTaskProgressDrawer/AsyncTaskProgressDrawer';
import { useTranslationCapManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { Anchor, Text } from '@mantine/core';
import { memo } from 'react';

interface Props {
	opened: boolean;
	onClose: () => void;
	status?: string;
	progressPercentage?: number;
	downloadUrl?: string;
	fileName?: string;
}

const ShareRegisterPdfDrawer = ({
	opened,
	onClose,
	status,
	progressPercentage,
	downloadUrl,
	fileName,
}: Props) => {
	const tCapManage = useTranslationCapManage();

	const doneBody = downloadUrl ? (
		<Text size="sm">
			{tCapManage('share_register_drawer.download_ready_prefix')}
			<Anchor
				href={downloadUrl}
				ml={4}
				onClick={(event) => {
					if (!fileName) {
						return;
					}
					event.preventDefault();
					void downloadUrlAsFile(downloadUrl, fileName);
				}}
			>
				{fileName ?? tCapManage('share_register_drawer.download_link_fallback')}
			</Anchor>
		</Text>
	) : null;

	return (
		<AsyncTaskProgressDrawer
			opened={opened}
			onClose={onClose}
			status={status}
			progressPercentage={progressPercentage}
			// The first ~5% is the cap-table rebuild + transform + shard (one
			// opaque step with no granular progress); show an indeterminate bar
			// there so it doesn't look stuck before the parallel render begins.
			indeterminateUntilPercentage={5}
			preparingLabel={tCapManage('share_register_drawer.preparing_detail')}
			title={tCapManage('share_register_drawer.title')}
			inProgressAlert={{
				title: tCapManage('share_register_drawer.generating_title'),
				description: tCapManage('share_register_drawer.generating_detail'),
			}}
			doneAlert={{
				title: tCapManage('share_register_drawer.done_title'),
				description: tCapManage('share_register_drawer.done_detail'),
			}}
			errorAlert={{
				title: tCapManage('share_register_drawer.error_title'),
				description: tCapManage('share_register_drawer.error_detail'),
			}}
			doneBody={doneBody}
			closeLabel={tCapManage('share_register_drawer.close_button')}
		/>
	);
};

const _ShareRegisterPdfDrawerExport = memo(ShareRegisterPdfDrawer);
export { _ShareRegisterPdfDrawerExport as ShareRegisterPdfDrawer };
